import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Agreement, AgreementStatus } from '@/domain/agreements/agreement'
import { Parcel, ParcelState, formatParcelState, getParcelStateColor } from '@/domain/parcels/parcel'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRightCircle, MapPin, PlusCircle } from 'lucide-react'

export const ParcelInfoSection = ({ parcel, agreement }: { parcel: Parcel, agreement: Agreement }) => {
  const navigate = useNavigate()
  const canBeOccupied = parcel.state === ParcelState.SOLD && agreement.status !== AgreementStatus.Terminated

  return (
    <div className="space-y-3 sm:col-span-2 border-t pt-4 mt-2">
      <div className="flex flex-col gap-3">
        <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Parcela vinculada
        </div>
        <div className="flex items-center justify-start sm:justify-between flex-wrap gap-5">
          <Button
            variant="outline"
            className="h-auto p-0 border-primary/20 hover:border-primary/50 hover:bg-primary/5 group transition-all duration-200 shadow-sm overflow-hidden flex-col items-stretch"
            onClick={() => navigate({
              to: `/app/management/contrataciones/${agreement.id}/parcela/${parcel.id}` as any
            })}
          >
            <div className="flex items-stretch w-full">
              <div className="font-mono font-medium text-gray-900 px-5 py-3 bg-gray-50/50 group-hover:bg-primary/10 transition-colors flex items-center gap-4 flex-1">
                <div className="flex flex-col items-center">
                  <span className="text-[10px] uppercase text-muted-foreground font-bold leading-none mb-1.5">Sección</span>
                  <span className="text-xl leading-none">{parcel.section}</span>
                </div>
                <div className="w-px h-10 bg-gray-200 group-hover:bg-primary/20" />
                <div className="flex flex-col items-center">
                  <span className="text-[10px] uppercase text-muted-foreground font-bold leading-none mb-1.5">Fila</span>
                  <span className="text-xl leading-none">{parcel.fila}</span>
                </div>
                <div className="w-px h-10 bg-gray-200 group-hover:bg-primary/20" />
                <div className="flex flex-col items-center">
                  <span className="text-[10px] uppercase text-muted-foreground font-bold leading-none mb-1.5">Parcela</span>
                  <span className="text-xl leading-none">{parcel.parcela}</span>
                </div>
              </div>
              <div className={`flex items-center px-5 py-3 bg-white group-hover:bg-primary/5 transition-colors border-l border-primary/10`}>
                <Badge className={getParcelStateColor(parcel.state, parcel.parcelContractState)}>
                  {formatParcelState(parcel.state, parcel.parcelContractState)}
                </Badge>
              </div>
              <div className="flex items-center px-5 py-3 bg-white group-hover:bg-primary/5 transition-colors border-l border-primary/10">
                <ArrowRightCircle className="h-5 w-5 text-primary" />
              </div>
            </div>
          </Button>
          {canBeOccupied && (
            <Button
              variant="destructive"
              size="sm"
              className="h-8"
              onClick={() => navigate({
                to: `/app/management/contrataciones/${agreement.id}/parcela/${parcel.id}/ocupar` as any
              })}
            >
              Ocupar parcela
            </Button>
          )}
          {parcel.state === ParcelState.OCCUPIED && (
            <Button
              variant="info"
              size="sm"
              className="h-8"
              onClick={() => navigate({
                to: `/app/management/contrataciones/${agreement.id}/parcela/${parcel.id}/adicionales` as any
              })}
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Agregar adicionales
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
